// Implements: F-UIX-024, F-UIX-010
/**
 * A mockup's applied design, and the explicit re-pin, for the indicator.
 *
 * The answer is keyed by mockup id (checklist 14): a re-pin of mockup A writes
 * its answer into A's entry only, so a page that has moved on to B never shows
 * A's pin. The re-pin never rejects: it answers the new view or the refusal.
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";
import { useCallback } from "react";

import type { ApiError } from "../../../shared/api/ApiError";
import { asApiError } from "../../../shared/api/asApiError";
import type { MockupDesignView, RepinInput } from "../../../types/design";
import { fetchMockupDesign, repinMockup } from "../api/designApi";

export function mockupDesignKey(mockupId: string): readonly ["design", "mockup", string] {
  return ["design", "mockup", mockupId];
}

export function useMockupDesign(
  mockupId: string,
): UseQueryResult<MockupDesignView | undefined, ApiError> {
  return useQuery<MockupDesignView | undefined, ApiError>({
    queryKey: mockupDesignKey(mockupId),
    queryFn: () => fetchMockupDesign(mockupId),
  });
}

export type RepinOutcome =
  | { readonly ok: true; readonly view: MockupDesignView | undefined }
  | { readonly ok: false; readonly error: ApiError };

export function useRepinMockup(): (mockupId: string, input: RepinInput) => Promise<RepinOutcome> {
  const queryClient = useQueryClient();

  return useCallback(
    async (mockupId: string, input: RepinInput): Promise<RepinOutcome> => {
      let view: MockupDesignView | undefined;
      try {
        view = await repinMockup(mockupId, input);
      } catch (cause) {
        return { ok: false, error: asApiError(cause) };
      }
      if (view !== undefined) queryClient.setQueryData(mockupDesignKey(mockupId), view);
      return { ok: true, view };
    },
    [queryClient],
  );
}
